import React, { useState, useEffect, useMemo } from 'react';
import api from './axiosConfig';
import {
  Box, Typography, Accordion, AccordionSummary, AccordionDetails, TextField, Button,
  Alert, Snackbar, CircularProgress, Chip, Grid, Paper, InputAdornment
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import LogoUpload from './LogoUpload';

// Settings > Documents. The fields come from the server (services/document_settings_schema.py),
// so adding a setting there needs no change here.
export default function DocumentSettings() {
  const [groups, setGroups] = useState([]);
  const [saved, setSaved] = useState({});      // key -> value as stored
  const [edits, setEdits] = useState({});      // key -> value changed in this session
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [query, setQuery] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/document-settings');
      const gs = res.data.groups || [];
      const values = {};
      gs.forEach((g) => g.fields.forEach((f) => { values[f.key] = f.value || ''; }));
      setGroups(gs);
      setSaved(values);
      setEdits({});
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load document settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const valueOf = (key) => (key in edits ? edits[key] : saved[key] || '');
  const dirtyKeys = useMemo(
    () => Object.keys(edits).filter((k) => (edits[k] || '').trim() !== (saved[k] || '').trim()),
    [edits, saved]
  );

  const handleChange = (key, value) => setEdits((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const settings = {};
      dirtyKeys.forEach((k) => { settings[k] = edits[k]; });
      await api.put('/document-settings', { settings });
      setToast('Document settings saved');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save document settings.');
    } finally {
      setSaving(false);
    }
  };

  const q = query.trim().toLowerCase();
  const visible = (f) => !q || f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q);

  if (loading) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>Document settings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Your salon name, address, phone, email and VAT come from Business Details, and holiday
        entitlement and holiday year come from there too. Everything else your documents need is
        here. Anything you leave blank is either filled from the defaults shown or left as a
        visible blank on the document for you to complete by hand.
      </Typography>

      <LogoUpload />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TextField
        size="small" fullWidth placeholder="Search settings" value={query}
        onChange={(e) => setQuery(e.target.value)} sx={{ mb: 2 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
      />

      {groups.map((g) => {
        const fields = g.fields.filter(visible);
        if (fields.length === 0) return null;
        const filled = g.fields.filter((f) => valueOf(f.key).trim()).length;
        return (
          <Accordion key={g.id} defaultExpanded={!!q || g.id === 'salon'} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1">{g.title}</Typography>
                <Chip size="small" label={`${filled} of ${g.fields.length} filled`}
                      color={filled === g.fields.length ? 'success' : 'default'} />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{g.description}</Typography>
              <Grid container spacing={2}>
                {fields.map((f) => (
                  <Grid item xs={12} md={f.type === 'textarea' ? 12 : 6} key={f.key}>
                    <TextField
                      fullWidth size="small" label={f.label}
                      multiline={f.type === 'textarea'} minRows={f.type === 'textarea' ? 3 : 1}
                      value={valueOf(f.key)}
                      onChange={(e) => handleChange(f.key, e.target.value)}
                      placeholder={f.default ? `Default: ${f.default}` : ''}
                      inputProps={{ maxLength: 2000 }}
                    />
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Paper elevation={3} sx={{ position: 'sticky', bottom: 16, mt: 3, p: 1.5, display: 'flex',
                                 alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {dirtyKeys.length === 0 ? 'No unsaved changes' : `${dirtyKeys.length} unsaved change${dirtyKeys.length === 1 ? '' : 's'}`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={() => setEdits({})} disabled={saving || dirtyKeys.length === 0}>Discard</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || dirtyKeys.length === 0}>
            {saving ? 'Saving...' : 'Save changes'}
          </Button>
        </Box>
      </Paper>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} />
    </Box>
  );
}